import React, { useLayoutEffect, useRef, useState } from "react";
import { formatPfRegistration, getPfStateConfig, pfRefusalError, PF_INVALID_CHAR_ERROR } from "./statutoryFormats";

/**
 * Masked EPF registration input. The state prefix ("TS/", "AP/", …) is locked
 * and supplied from `state`; "/" separators are inserted automatically.
 *
 * `value` is the stored code ("" while only the prefix is shown).
 * `onInvalid(message)` fires as soon as a keystroke or paste is refused
 * (invalid character, wrong section type, wrong state, overflow).
 */
export default function PfRegistrationInput({ state, value, onChange, onBlur, onInvalid, className }) {
  const cfg = getPfStateConfig(state);
  const lead = `${cfg.prefix}/`;
  const ref = useRef(null);
  const caretRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const code = value || "";
  const display = code || (focused ? lead : "");
  // Values saved before state-aware formatting (e.g. "MH/BAN/…") are edited
  // freely until they are cleared or carry the selected state's prefix.
  const masked = !code || code.startsWith(lead);

  useLayoutEffect(() => {
    const pos = caretRef.current;
    if (pos == null || !ref.current) return;
    caretRef.current = null;
    if (document.activeElement === ref.current) ref.current.setSelectionRange(pos, pos);
  });

  const commit = (next, caret) => {
    const stored = next === lead ? "" : next;
    if (stored !== code) {
      caretRef.current = caret;
      onChange(stored);
    } else {
      // No re-render: React restores the controlled value, then we put the caret back.
      queueMicrotask(() => {
        if (ref.current && document.activeElement === ref.current) ref.current.setSelectionRange(caret, caret);
      });
    }
  };

  const caretFor = (src, pos, opts) => {
    const res = formatPfRegistration(src.slice(0, pos), state, opts);
    return res ? res.value.length : pos;
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const inputType = e.nativeEvent?.inputType || "";
    const isDelete = inputType.startsWith("delete") || raw.length < display.length;
    const opts = { trailingSlash: !isDelete };

    if (!masked) {
      const cleaned = raw.toUpperCase().replace(/[^A-Z0-9/]/g, "");
      if (!cleaned) return commit("", 0);
      if (!cleaned.startsWith(lead) && /^[A-Z]{2}\//.test(cleaned)) {
        const removed = raw.slice(0, caret).length - raw.slice(0, caret).toUpperCase().replace(/[^A-Z0-9/]/g, "").length;
        return commit(cleaned, caret - removed);
      }
      // Cleared down to a plain body, or now carries the right prefix → mask it.
      const src = cleaned.startsWith(lead) ? cleaned : lead + cleaned;
      const res = formatPfRegistration(src, state, opts);
      return commit(res.value, res.value.length);
    }

    let src = raw;
    let pos = caret;
    const restoreCaret = Math.max(lead.length, caret - (raw.length - display.length));
    if (!raw.toUpperCase().startsWith(lead)) {
      if (!isDelete) {
        onInvalid?.(cfg.error);
        return commit(display, restoreCaret);
      }
      // Deletion reached into the locked prefix — only remove the part that lies in the body.
      const start = Math.max(caret, lead.length);
      const end = Math.max(caret + (display.length - raw.length), start);
      src = display.slice(0, start) + display.slice(end);
      pos = start;
    }

    const res = formatPfRegistration(src, state, opts);
    if (!res) return commit(display, lead.length);
    // Refuse insertions with invalid characters, or characters that do not fit
    // the current section (letters in the establishment number, overflow, …).
    if (!isDelete && (res.invalid > 0 || res.dropped > 0)) {
      onInvalid?.(pfRefusalError(res, state));
      return commit(display, restoreCaret);
    }
    return commit(res.value, caretFor(src, pos, opts));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    // Surrounding whitespace is trimmed; anything else outside A–Z / 0–9 / "/" is refused.
    const pasted = (e.clipboardData?.getData("text") || "").trim().toUpperCase();
    if (!pasted) return;
    if (/[^A-Z0-9/]/.test(pasted)) {
      onInvalid?.(PF_INVALID_CHAR_ERROR);
      return;
    }

    let body = pasted;
    let whole = !masked;
    // Office codes are 3 letters, so a leading "XX/" can only be a state prefix.
    const m = pasted.match(/^([A-Z]{2})\//);
    if (m) {
      if (m[1] !== cfg.prefix) {
        onInvalid?.(cfg.error);
        return;
      }
      body = pasted.slice(lead.length);
      whole = true;
    }

    const el = e.currentTarget;
    const cur = masked ? display || lead : lead;
    let start = whole ? lead.length : Math.max(el.selectionStart ?? cur.length, lead.length);
    let end = whole ? cur.length : Math.max(el.selectionEnd ?? cur.length, start);
    const src = cur.slice(0, start) + body + cur.slice(end);
    const res = formatPfRegistration(src, state);
    if (!res || res.dropped > 0) {
      onInvalid?.(res ? pfRefusalError(res, state) : cfg.error);
      return;
    }
    commit(res.value, caretFor(src, start + body.length));
  };

  // Keep a collapsed caret out of the locked prefix (clicks / Home key).
  const handleSelect = (e) => {
    const el = e.currentTarget;
    if (!masked || el.selectionStart !== el.selectionEnd) return;
    if (el.selectionStart < lead.length && el.value.startsWith(lead)) el.setSelectionRange(lead.length, lead.length);
  };

  return (
    <input
      ref={ref}
      className={className}
      value={display}
      placeholder={cfg.placeholder}
      inputMode="text"
      autoComplete="off"
      spellCheck={false}
      onChange={handleChange}
      onPaste={handlePaste}
      onSelect={handleSelect}
      onFocus={() => {
        setFocused(true);
        caretRef.current = (code || lead).length;
      }}
      onBlur={() => {
        setFocused(false);
        // Drop the auto-inserted trailing "/" once the user leaves the field.
        const trimmed = masked ? code.replace(/\/+$/, "") : code;
        const next = trimmed === cfg.prefix ? "" : trimmed;
        if (next !== code) onChange(next);
        onBlur?.(next);
      }}
    />
  );
}
